import { useState, useRef, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { useScrollToTop } from '@/hooks/useScrollToTop';

export default function AboutPage() {
  useScrollToTop();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qs1avnahb7382ovei0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          subject: formData.get('subject') as string,
          message: formData.get('message') as string,
          form_name: '黑料社区联系我们',
        }).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后再试');
    }
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '黑料社区是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黑料社区是专注于明星八卦、网红爆料、娱乐圈内幕的内容聚集平台，用户可以自由浏览、分享各类热门人物的最新动态与独家内幕。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向黑料社区投稿爆料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系我们表单提交爆料内容，或直接发送邮件至 tips@heiliaoshequ.com。我们承诺对爆料人信息严格保密。',
        },
      },
      {
        '@type': 'Question',
        name: '黑料社区的内容真实性如何保证？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们鼓励用户提供真实可靠的爆料信息。所有内容仅供娱乐参考，不代表本平台立场。对于存在争议的内容，我们会在核实后标注相关信息。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在黑料社区上搜索感兴趣的内容？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的分类导航浏览不同类别的内容，包括明星八卦、网红曝光、娱乐圈内幕、热议话题等频道。',
        },
      },
      {
        '@type': 'Question',
        name: '黑料社区是否收费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黑料社区对所有用户完全免费开放，您无需支付任何费用即可浏览全部内容。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解黑料社区的品牌故事、社区声誉与联系方式。黑料社区是专注于明星八卦、网红爆料、娱乐圈内幕的内容聚集平台，致力于为娱乐爱好者提供最新、最全、最劲爆的娱乐圈资讯。"
        keywords="黑料社区关于我们,黑料社区品牌介绍,黑料社区联系方式,娱乐爆料平台介绍"
        canonicalUrl="/about"
      />
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-5xl mx-auto px-4 md:px-6 pt-24 pb-16">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Brand Introduction */}
          <section className="mb-12">
            <div className="bg-background-100 border border-background-300/60 rounded-2xl p-6 md:p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-primary-500/20 rounded-xl">
                  <i className="ri-building-2-line text-primary-400 text-2xl"></i>
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-100">
                    关于黑料社区
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">明星八卦 · 网红爆料 · 娱乐圈内幕</p>
                </div>
              </div>

              <div className="space-y-4 text-sm text-foreground-400 leading-relaxed">
                <p>
                  <strong className="text-foreground-200">黑料社区</strong>成立于2024年，是国内领先的娱乐八卦与明星爆料内容聚集平台。我们致力于为广大娱乐爱好者提供一个自由、开放、真实的资讯交流社区。
                </p>
                <p>
                  在黑料社区，用户可以自由浏览、分享和讨论各类热门人物的最新动态与独家内幕。无论是<strong className="text-foreground-200">明星八卦</strong>、<strong className="text-foreground-200">网红曝光</strong>，还是<strong className="text-foreground-200">娱乐圈内幕</strong>，我们都力求第一时间为您呈现最劲爆、最真实的资讯内容。
                </p>
                <p>
                  我们的团队由资深娱乐记者、网络爆料达人和技术专家组成，通过多渠道信息采集和严格的审核机制，确保平台内容的丰富性和多样性。我们相信，每个人都有了解真相的权利，每个声音都值得被倾听。
                </p>
                <p>
                  黑料社区始终秉持<strong className="text-foreground-200">真实、开放、有趣</strong>的价值观，努力打造中文互联网最具影响力的娱乐资讯平台。
                </p>
              </div>
            </div>
          </section>

          {/* Reputation & Values */}
          <section className="mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-200 mb-6 flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-shield-star-line text-accent-500 text-xl"></i>
              </div>
              社区声誉
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户需求为核心，持续优化阅读体验和社区互动功能' },
                { icon: 'ri-shield-check-line', title: '隐私保护', desc: '严格保护爆料人隐私，所有投稿均可匿名提交，信息安全有保障' },
                { icon: 'ri-verified-badge-line', title: '信息核实', desc: '建立多渠道信息核实机制，尽最大努力确保内容真实性' },
                { icon: 'ri-community-line', title: '社区共建', desc: '鼓励用户参与内容创作与讨论，共同打造健康活跃的社区氛围' },
              ].map((item) => (
                <div
                  key={item.title}
                  className="bg-background-100 border border-background-300/60 rounded-xl p-5 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-500/20 rounded-full">
                    <i className={`${item.icon} text-accent-400 text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-200 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Stats */}
          <section className="mb-12">
            <div className="bg-background-100 border border-background-300/60 rounded-2xl p-6 md:p-10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                {[
                  { value: '10万+', label: '注册用户', icon: 'ri-user-line' },
                  { value: '5000+', label: '独家爆料', icon: 'ri-file-text-line' },
                  { value: '99.9%', label: '服务可用率', icon: 'ri-cloud-line' },
                  { value: '24h', label: '资讯更新', icon: 'ri-timer-flash-line' },
                ].map((stat) => (
                  <div key={stat.label}>
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-500/20 rounded-full">
                      <i className={`${stat.icon} text-primary-400 text-lg`}></i>
                    </div>
                    <div className="text-xl md:text-2xl font-heading font-bold text-primary-400 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-200 mb-6 flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-question-line text-secondary-400 text-xl"></i>
              </div>
              常见问题
            </h2>

            <div className="flex flex-col gap-4">
              {[
                { q: '黑料社区是什么？', a: '黑料社区是专注于明星八卦、网红爆料、娱乐圈内幕的内容聚集平台，用户可以自由浏览、分享各类热门人物的最新动态与独家内幕。' },
                { q: '如何向黑料社区投稿爆料？', a: '您可以通过本页面的联系我们表单提交爆料内容，或直接发送邮件至 tips@heiliaoshequ.com。我们承诺对爆料人信息严格保密。' },
                { q: '黑料社区的内容真实性如何保证？', a: '我们鼓励用户提供真实可靠的爆料信息。所有内容仅供娱乐参考，不代表本平台立场。对于存在争议的内容，我们会在核实后标注相关信息。' },
                { q: '如何在黑料社区上搜索感兴趣的内容？', a: '您可以通过首页的分类导航浏览不同类别的内容，包括明星八卦、网红曝光、娱乐圈内幕、热议话题等频道。' },
                { q: '黑料社区是否收费？', a: '黑料社区对所有用户完全免费开放，您无需支付任何费用即可浏览全部内容。' },
              ].map((faq, i) => (
                <details
                  key={i}
                  className="bg-background-100 border border-background-300/60 rounded-xl group cursor-pointer"
                >
                  <summary className="px-5 py-4 text-sm font-semibold text-foreground-200 list-none flex items-center justify-between">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-400 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="mb-12">
            <h2 className="text-lg font-heading font-bold text-foreground-200 mb-6 flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-mail-send-line text-primary-400 text-xl"></i>
              </div>
              联系我们
            </h2>

            <div className="bg-background-100 border border-background-300/60 rounded-2xl p-6 md:p-10">
              <p className="text-sm text-foreground-500 mb-6 leading-relaxed">
                如果您有爆料线索、合作意向或任何问题，欢迎通过以下表单与我们联系。我们会在24小时内回复您。
              </p>

              {formStatus === 'success' ? (
                <div className="bg-accent-100/20 border border-accent-500/30 rounded-xl p-8 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-500/20 rounded-full">
                    <i className="ri-checkbox-circle-line text-accent-400 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-accent-400 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-500">感谢您的来信，我们会在24小时内回复您。</p>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-5" data-readdy-form="">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="antispam-field"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm text-foreground-300 mb-2">
                        您的姓名 <span className="text-primary-400">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder="请输入您的姓名"
                        className="w-full px-4 py-2.5 bg-background-200 border border-background-300/60 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm text-foreground-300 mb-2">
                        邮箱地址 <span className="text-primary-400">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        placeholder="请输入您的邮箱"
                        className="w-full px-4 py-2.5 bg-background-200 border border-background-300/60 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm text-foreground-300 mb-2">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      placeholder="请输入主题"
                      className="w-full px-4 py-2.5 bg-background-200 border border-background-300/60 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm text-foreground-300 mb-2">
                      留言内容 <span className="text-primary-400">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="请详细描述您的需求或爆料内容（最多500字）"
                      className="w-full px-4 py-2.5 bg-background-200 border border-background-300/60 rounded-lg text-sm text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-600 mt-1">最多500个字符</p>
                  </div>

                  {formStatus === 'error' && (
                    <div className="bg-primary-500/10 border border-primary-500/30 rounded-lg p-3">
                      <p className="text-sm text-primary-400">{formError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-send-plane-line mr-2"></i>提交留言
                  </button>
                </form>
              )}
            </div>
          </section>

          {/* Contact Info */}
          <section>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="bg-background-100 border border-background-300/60 rounded-xl p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-500/20 rounded-full">
                  <i className="ri-mail-line text-primary-400 text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-200 mb-1">爆料邮箱</h4>
                <a href="mailto:tips@heiliaoshequ.com" className="text-xs text-foreground-500 hover:text-primary-400 transition-colors whitespace-nowrap">
                  tips@heiliaoshequ.com
                </a>
              </div>
              <div className="bg-background-100 border border-background-300/60 rounded-xl p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-accent-500/20 rounded-full">
                  <i className="ri-shield-check-line text-accent-400 text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-200 mb-1">隐私保障</h4>
                <p className="text-xs text-foreground-500">匿名爆料，信息严格保密</p>
              </div>
              <div className="bg-background-100 border border-background-300/60 rounded-xl p-5 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-secondary-500/20 rounded-full">
                  <i className="ri-time-line text-secondary-400 text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-200 mb-1">响应时间</h4>
                <p className="text-xs text-foreground-500">24小时内快速响应</p>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}